import { api, fetchWithAuth } from './api';

// ---------------------------------------------------------------------------
// Stream delimiter parser
// Parses chunks looking for ---SECTION_NAME--- markers
// Handles delimiters split across chunk boundaries via buffering
// ---------------------------------------------------------------------------

export type SectionCallback = (section: string, content: string) => void;
export type CompleteCallback = (sections: Record<string, string>) => void;

const DELIMITER_RE = /---([A-Z_]+)---/;

export async function streamMindRequest(
  url: string,
  body: unknown,
  onSection: SectionCallback,
  onComplete: CompleteCallback,
  signal: AbortSignal,
): Promise<void> {
  let response: Response;
  try {
    response = await fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(body),
      signal,
    });
  } catch (err: unknown) {
    if (err instanceof Error && err.name === 'AbortError') return;
    throw err;
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();

  let buffer = '';
  let currentSection = '';
  let currentContent = '';
  const allSections: Record<string, string> = {};

  const flushSection = (section: string, content: string) => {
    if (section) {
      const trimmed = content.trimEnd();
      allSections[section] = trimmed;
      onSection(section, trimmed);
    }
  };

  try {
    while (true) {
      let done: boolean;
      let value: Uint8Array | undefined;
      try {
        ({ done, value } = await reader.read());
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return;
        throw err;
      }

      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process buffer: scan for complete delimiters
      // A delimiter looks like ---SECTION_NAME--- and may be split across chunks
      // We scan character by character looking for complete --- ... --- patterns
      let pos = 0;
      while (pos < buffer.length) {
        const delimStart = buffer.indexOf('---', pos);
        if (delimStart === -1) {
          // No delimiter start found — consume all remaining as content
          currentContent += buffer.slice(pos);
          buffer = '';
          pos = 0;
          break;
        }

        // Consume text before the delimiter start as content
        if (delimStart > pos) {
          currentContent += buffer.slice(pos, delimStart);
        }

        // Look for closing ---
        const delimEnd = buffer.indexOf('---', delimStart + 3);
        if (delimEnd === -1) {
          // Incomplete delimiter — keep from delimStart in buffer and stop
          buffer = buffer.slice(delimStart);
          pos = 0;
          break;
        }

        const potentialDelim = buffer.slice(delimStart, delimEnd + 3);
        const match = DELIMITER_RE.exec(potentialDelim);

        if (match) {
          // Valid delimiter found
          flushSection(currentSection, currentContent);
          currentSection = match[1];
          currentContent = '';
          pos = delimEnd + 3;
          // Skip leading newline after delimiter
          if (pos < buffer.length && buffer[pos] === '\n') pos++;
        } else {
          // Not a valid delimiter (e.g., "--- text ---"), treat as content
          currentContent += buffer.slice(delimStart, delimEnd + 3);
          pos = delimEnd + 3;
        }
      }

      if (pos > 0 && pos <= buffer.length) {
        buffer = buffer.slice(pos);
      }
    }

    // Flush any remaining buffer as content
    if (buffer) {
      currentContent += buffer;
    }

    // Flush final section
    flushSection(currentSection, currentContent);
    onComplete(allSections);
  } catch (err: unknown) {
    if (err instanceof Error && err.name === 'AbortError') return;
    throw err;
  }
}

// ---------------------------------------------------------------------------
// REST API wrappers
// ---------------------------------------------------------------------------

export const mindApi = {
  selectVersion: (sessionId: number, version: string, thinkingLevel: string, style: string) =>
    api.post(`/mind/question-workshop/${sessionId}/select`, {
      version,
      thinking_level: thinkingLevel,
      style,
    }),

  copyVersion: (sessionId: number, version: string) =>
    api.post(`/mind/question-workshop/${sessionId}/copy`, { version }),

  saveCapture: (data: {
    type: string;
    title: string;
    body: string;
    tags?: string[];
    domain?: string;
    metadata?: Record<string, unknown>;
  }) => api.post('/mind/capture', data),

  updateEntry: (id: number, fields: {
    title?: string;
    body?: string;
    tags?: string[];
    domain?: string;
    metadata?: Record<string, unknown>;
  }) => api.put(`/mind/entries/${id}`, fields),

  getEntries: (filters?: {
    type?: string;
    domain?: string;
    tag?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.type) params.set('type', filters.type);
    if (filters?.domain) params.set('domain', filters.domain);
    if (filters?.tag) params.set('tag', filters.tag);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.limit != null) params.set('limit', String(filters.limit));
    if (filters?.offset != null) params.set('offset', String(filters.offset));
    const qs = params.toString();
    return api.get(`/mind/entries${qs ? `?${qs}` : ''}`);
  },

  getDomains: () => api.get('/mind/domains'),

  getDashboard: () => api.get('/mind/dashboard'),

  exportMarkdown: () =>
    fetchWithAuth('/api/mind/export').then((r) => r.text()),

  importMarkdown: (text: string) =>
    api.post('/mind/import', { markdown: text }),

  getRetrospectives: () => api.get('/mind/retrospectives'),
};
